import { useState, useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { CalendarEvent, CalendarFilters, CalendarView } from './types';
import { calendarService } from './calendar.service';
import { useAuth } from '@/contexts/auth.context';
import { useServiceStatus } from '@/contexts/service-status';
import { BankRoles, ProviderRoles, InspectorRoles } from '@/shared/enums/UserRoles';

export function useCalendarData() {
  const { user } = useAuth();
  const { isServiceOnline } = useServiceStatus();
  const isInspectionServiceOnline = isServiceOnline('inspection');

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [filters, setFilters] = useState<CalendarFilters>({});
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Determinar role do usuário
  const userRole = useMemo(() => {
    if (!user) return null;

    const roles = user.roles || [];

    if (roles.includes(BankRoles.ADMIN_BANK) || roles.includes(BankRoles.BANK_OPERATIONAL)) {
      return 'bank';
    }

    if (roles.includes(ProviderRoles.MASTER_PROVIDER)) {
      return 'master_provider';
    }

    if (roles.includes(ProviderRoles.PROVIDER_OPERATIONAL)) {
      return 'operational_provider';
    }

    if (roles.includes(InspectorRoles.INSPECTOR)) {
      return 'inspector';
    }

    return null;
  }, [user]);

  // MULTI-TENANCY: Aplicar filtros automáticos baseados no role do usuário
  const effectiveFilters = useMemo(() => {
    const baseFilters = { ...filters };

    // Para inspetores: filtrar apenas suas próprias vistorias
    if (userRole === 'inspector' && user?.id) {
      baseFilters.inspectorId = user.id;
    }

    // Para prestadoras operacionais: filtrar por providerId do usuário
    // Apenas se o usuário não selecionou um filtro específico de inspetor
    if (userRole === 'operational_provider' && user?.providerId && !baseFilters.inspectorId) {
      baseFilters.providerId = user.providerId;
    }

    // Para prestadoras master: filtrar por providerId do usuário
    // Apenas se o usuário não selecionou filtros específicos
    if (userRole === 'master_provider' && user?.providerId && !baseFilters.providerId) {
      baseFilters.providerId = user.providerId;
    }

    return baseFilters;
  }, [filters, userRole, user]);

  // Calcular intervalo de datas baseado na view atual
  const dateRange = useMemo(() => {
    const current = dayjs(currentDate);

    switch (view) {
      case 'month':
        return {
          start: current.startOf('month').subtract(7, 'days').toDate(),
          end: current.endOf('month').add(7, 'days').toDate(),
        };
      case 'week':
        return {
          start: current.startOf('week').toDate(),
          end: current.endOf('week').toDate(),
        };
      case 'day':
        return {
          start: current.startOf('day').toDate(),
          end: current.endOf('day').toDate(),
        };
      case 'agenda':
        return {
          start: current.toDate(),
          end: current.add(1, 'month').toDate(),
        };
      default:
        return {
          start: current.startOf('month').toDate(),
          end: current.endOf('month').toDate(),
        };
    }
  }, [currentDate, view]);

  // Carregar eventos
  const loadEvents = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    setLoading(true);
    try {
      const calendarEvents = await calendarService.getCalendarEvents(
        dateRange.start,
        dateRange.end,
        effectiveFilters
      );

      setEvents(calendarEvents);
    } catch (error: any) {
      message.error(error.message || 'Erro ao carregar eventos do calendário');
    } finally {
      setLoading(false);
    }
  }, [dateRange, effectiveFilters, view, userRole, user, isInspectionServiceOnline]);

  // Carregar eventos quando data ou filtros mudarem
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Handlers
  const handleNavigate = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView: CalendarView) => {
    setView(newView);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<CalendarFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleEventSelect = useCallback(async (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDrawerVisible(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setSelectedEvent(null);
  }, []);

  const handleRefresh = useCallback(() => {
    loadEvents();
  }, [loadEvents]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handlePrevious = useCallback(() => {
    const current = dayjs(currentDate);
    switch (view) {
      case 'month':
        setCurrentDate(current.subtract(1, 'month').toDate());
        break;
      case 'week':
        setCurrentDate(current.subtract(1, 'week').toDate());
        break;
      case 'day':
        setCurrentDate(current.subtract(1, 'day').toDate());
        break;
      case 'agenda':
        setCurrentDate(current.subtract(1, 'month').toDate());
        break;
    }
  }, [currentDate, view]);

  const handleNext = useCallback(() => {
    const current = dayjs(currentDate);
    switch (view) {
      case 'month':
        setCurrentDate(current.add(1, 'month').toDate());
        break;
      case 'week':
        setCurrentDate(current.add(1, 'week').toDate());
        break;
      case 'day':
        setCurrentDate(current.add(1, 'day').toDate());
        break;
      case 'agenda':
        setCurrentDate(current.add(1, 'month').toDate());
        break;
    }
  }, [currentDate, view]);

  const handleDateSelect = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  return {
    events,
    loading,
    currentDate,
    view,
    filters,
    selectedEvent,
    drawerVisible,
    userRole,
    user,
    dateRange,
    handleNavigate,
    handleViewChange,
    handleFilterChange,
    handleEventSelect,
    handleDrawerClose,
    handleRefresh,
    handleToday,
    handlePrevious,
    handleNext,
    handleDateSelect,
  };
}
